// AI assisted development
/**
 * Floating feedback entry point for every dashboard page.
 */

import { useMemo, useState } from 'react';
import { Fab, Tooltip } from '@mui/material';
import { Feedback as FeedbackIcon } from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import FeedbackFormDialog from './FeedbackFormDialog';

const ROUTE_LABELS: Record<string, string> = {
  '/organization': 'Organization Dashboard',
  '/engineers': 'Engineer Directory',
  '/report-card': 'My Report Card',
  '/pr-history': 'Pull Requests',
  '/issues': 'Issues',
  '/repos': 'Repos',
  '/teams': 'Teams',
  '/errors': 'Error Details',
  '/feedback': 'Feedback',
  '/release-notes': 'Release Notes',
  '/admin': 'Admin',
  '/devagent/dashboard': 'DevAgent Dashboard',
  '/devagent/pipelines': 'DevAgent Pipelines',
  '/devagent/create': 'Create DevAgent Pipeline',
  '/devagent/analytics': 'DevAgent Analytics',
};

function getPageTitle(pathname: string): string {
  if (ROUTE_LABELS[pathname]) return ROUTE_LABELS[pathname];
  if (pathname.startsWith('/devagent/pipelines/')) return 'DevAgent Pipeline Detail';
  if (pathname.startsWith('/issues/')) return 'Issue Detail';
  if (pathname.startsWith('/repos/')) return 'Repository Detail';
  if (pathname.startsWith('/teams/')) return 'Team Detail';
  return 'Monocle Dashboard';
}

export default function PageFeedbackButton() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const shouldSuppress = location.pathname === '/feedback';

  const pageContext = useMemo(() => {
    const pageUrl = `${location.pathname}${location.search}`;
    return {
      pageTitle: getPageTitle(location.pathname),
      pageUrl,
      source: location.pathname.startsWith('/devagent') ? 'DevAgent' : 'Monocle',
    };
  }, [location.pathname, location.search]);

  if (shouldSuppress) {
    return null;
  }

  return (
    <>
      <Tooltip title="Share feedback">
        <Fab
          color="primary"
          aria-label="Share feedback"
          onClick={() => setOpen(true)}
          size="medium"
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: (theme) => theme.zIndex.drawer + 2,
          }}
        >
          <FeedbackIcon />
        </Fab>
      </Tooltip>
      <FeedbackFormDialog
        open={open}
        onClose={() => setOpen(false)}
        pageTitle={pageContext.pageTitle}
        pageUrl={pageContext.pageUrl}
        source={pageContext.source}
      />
    </>
  );
}
